import { notification } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTES } from 'shared/constants';
import styles from './communication-sent-items.module.scss';
import { useNavigate } from 'react-router-dom';
import { RecipientsPreviewModal } from '../recipients-group-selector/recipients-preview-modal';
import { getLang } from 'shared/store/selectors/lang.selector';
import { deleteCommunicationItem } from 'shared/store/actions/communication-center.actions';
import { Loader } from 'src/components/core';
import communicationServiceApi from 'shared/api/communication.service.api';
import { formatFullName } from 'shared/utils';
import update from 'immutability-helper';
import {
  AG_FILTER_TYPES,
  Table,
  cellActionKeyboardNavigate,
  SetFilter,
  setFilterDoesFilterPass,
  textWithFallback,
  DateCell,
  UserLinkCell,
  DeleteActionCell,
} from 'src/components/design-system/table';

export function CommunicationSentItems() {
  const [messagesList, setMessagesList] = React.useState(null);
  const [api, contextHolder] = notification.useNotification({ placement: 'bottomRight' });
  const [deleting, setDeleting] = React.useState({});
  const [recipients, setRecipients] = React.useState(null);
  const lang = useSelector(getLang('COMMUNICATION_CENTER'));

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchMessagesList = async () => {
      const { data, error } = await communicationServiceApi.getSentItems();

      if (error) {
        api.error({
          title: lang.MESSAGE_FETCH_ERROR,
          placement: 'bottomRight',
          duration: 3,
        });
        setMessagesList([]);
        return;
      }

      setMessagesList(data?.messages);
    };
    fetchMessagesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemove = useCallback(
    async (e, id) => {
      e.stopPropagation();
      e.preventDefault();
      setDeleting(prev => ({ ...prev, [id]: true }));

      const { error } = await dispatch(deleteCommunicationItem({ id }));

      setDeleting(prev => ({ ...prev, [id]: false }));

      if (error) {
        api.error({
          title: lang.MESSAGE_DELETE_ERROR,
          placement: 'bottomRight',
          duration: 3,
        });
        return;
      }

      setMessagesList(state =>
        update(state, { $splice: [[state.findIndex(item => item.id === id), 1]] }),
      );

      api.success({
        title: lang.MESSAGE_DELETE_SUCCESS,
        placement: 'bottomRight',
        duration: 3,
      });
    },
    [api, dispatch, lang.MESSAGE_DELETE_ERROR, lang.MESSAGE_DELETE_SUCCESS],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: lang.SENT_AT,
        field: 'createdAt',
        cellRenderer: DateCell,
        cellRendererParams: { format: 'smartDateTime' },
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: lang.SUBJECT,
        field: 'title',
        filter: AG_FILTER_TYPES.TEXT,
        valueFormatter: textWithFallback,
        flex: 2,
      },
      {
        headerName: lang.FROM,
        field: 'sentByUser',
        valueGetter: params => formatFullName(params.data.sentByUser),
        filter: {
          component: SetFilter,
          doesFilterPass: setFilterDoesFilterPass,
        },
        cellRenderer: UserLinkCell,
        cellRendererParams: { userField: 'sentByUser', className: styles.linkToUser },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: lang.ATTACHMENTS,
        field: 'attachmentsCount',
        filter: AG_FILTER_TYPES.NUMBER,
        cellRenderer: ({ value }) => (value > 0 ? value : '-'),
        minWidth: 130,
        flex: 1,
      },
      {
        headerName: lang.ACTIONS || 'Actions',
        field: 'id',
        cellRenderer: ({ value }) => (
          <DeleteActionCell
            itemId={value}
            isDeleting={deleting[value]}
            onDelete={handleRemove}
            lang={{
              confirmTitle: lang.MESSAGE_DELETE_CONFIRMATION,
              deleteLabel: lang.DELETE_COMMUNICATION,
              deleteButton: lang.DELETE_COMMUNICATION_ACTION,
              cancelButton: lang.CANCEL_ACTION,
            }}
            className={styles.actionsCell}
          />
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        minWidth: 80,
        flex: 1,
        sortable: false,
      },
    ],
    [lang, deleting, handleRemove],
  );

  const navigateToItem = useCallback(
    id => navigate(ROUTES.COMMUNICATION_ITEM.replace(':id', id)),
    [navigate],
  );

  const handleCellClicked = useCallback(
    params => {
      if (params.colDef.suppressKeyboardEvent) return;
      navigateToItem(params.data.id);
    },
    [navigateToItem],
  );

  const handleCellKeyDown = useCallback(
    params => {
      if (params.event.key === 'Enter' && !params.colDef.suppressKeyboardEvent?.(params)) {
        navigateToItem(params.data.id);
      }
    },
    [navigateToItem],
  );

  if (messagesList === null) {
    return <Loader />;
  }

  return (
    <>
      {contextHolder}
      <Table
        containerProps={{ className: styles.tableContainer }}
        columnDefs={columnDefs}
        rowData={messagesList}
        getRowId={params => params.data.id}
        onCellClicked={handleCellClicked}
        onCellKeyDown={handleCellKeyDown}
        rowSelection={{
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        }}
      />

      {recipients !== null && (
        <RecipientsPreviewModal
          userIds={recipients !== null && recipients?.map(({ id }) => id)}
          open={recipients !== null}
          closeModal={() => setRecipients(null)}
        />
      )}
    </>
  );
}
